import React, { useRef, useState } from "react";
import "../styles/changeProfilePictureModal.scss";

/**
 * ChangeProfilePictureModal — Dark card modal for uploading profile picture
 * Figma: Overlay (Desktop) — Account page overlay
 *
 * Dark card with:
 *   - "Change profile picture" title + description
 *   - Close (X) button top right
 *   - Drag-and-drop file upload area with cloud icon
 *   - "Close" button bottom right
 *
 * @param {boolean} isOpen — show/hide modal
 * @param {function} onClose — close handler
 */

const ChangeProfilePictureModal = ({ isOpen, onClose }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState(null);

  if (!isOpen) return null;

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setFileName(file.name);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
    }
  };

  return (
    <div className="change-pic-overlay" onClick={onClose}>
      <div className="change-pic-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="change-pic-modal__header">
          <div className="change-pic-modal__header-text">
            <h2 className="change-pic-modal__title">Change profile picture</h2>
            <p className="change-pic-modal__desc">
              Subtitle display settings such as text color, text size, and text background
            </p>
          </div>
          <button className="change-pic-modal__close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="#a3a3a3" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Drag-and-drop upload area */}
        <div
          className={`change-pic-modal__dropzone ${isDragging ? "change-pic-modal__dropzone--active" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleBrowseClick}
        >
          {/* Cloud upload icon */}
          <svg className="change-pic-modal__upload-icon" width="40" height="40" viewBox="0 0 40 40" fill="none">
            <path
              d="M13.3 30H10C6.68 30 4 27.32 4 24C4 21.08 6.06 18.64 8.82 18.1C8.28 17.14 8 16.06 8 14.94C8 11.08 11.08 8 14.94 8C17.14 8 19.14 9.06 20.42 10.72C21.34 10.26 22.38 10 23.48 10C27.34 10 30.42 13.08 30.42 16.94C30.42 17.4 30.38 17.84 30.3 18.28C33.26 18.98 35.42 21.64 35.42 24.82C35.42 28.54 32.38 31.58 28.66 31.58H26.66"
              stroke="#737373"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M20 22V34M20 22L24 26M20 22L16 26"
              stroke="#737373"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          <h3 className="change-pic-modal__dropzone-title">
            {fileName ? fileName : "Drag and drop file"}
          </h3>
          <p className="change-pic-modal__dropzone-desc">
            Browse and choose the files you want to upload from your computer
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="change-pic-modal__file-input"
            onChange={handleFileChange}
          />
        </div>

        {/* Footer */}
        <div className="change-pic-modal__footer">
          <button className="change-pic-modal__close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeProfilePictureModal;
